import { useState, useEffect, useMemo, useCallback } from 'react'
import { useReferenceDescription } from '../deposit-split-grid'
import { DepositSplitRow } from '../deposit-split-grid/deposit-split.types'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { FormControlLabel, Grid, RadioGroup, Radio } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
type DepositSplitDeleteDialogProps = {
  data: DepositSplitRow[]
  deleteId: string
  onConfirm: (rowToUpdate: DepositSplitRow[]) => void
  onClose: () => void
}

export const DepositSplitDeleteDialog = (props: DepositSplitDeleteDialogProps) => {
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency()
  const { currency } = useTenant()
  const getReferenceDescription = useReferenceDescription()

  const [localData, setLocalData] = useState<DepositSplitRow[]>()
  const [destinationRowId, setDestinationRowId] = useState<string>()

  useEffect(() => {
    const temp = props.data.filter((i) => i.id !== props.deleteId)
    setLocalData(temp)
    setDestinationRowId(temp[0].id)
  }, [props.data, props.deleteId])

  const rowToDelete = useMemo(() => {
    return props.data.find((i) => i.id === props.deleteId)
  }, [props.data, props.deleteId])

  const onSubmit = useCallback(() => {
    if (localData && destinationRowId && rowToDelete) {
      const rowToUpdate = [...localData].map((i) => {
        if (i.id === destinationRowId) {
          return { ...i, amount: i.amount + rowToDelete.amount }
        } else {
          return i
        }
      })
      props.onConfirm(rowToUpdate)
    }
  }, [localData, destinationRowId, rowToDelete])

  return (
    <FormLayout
      title={t('pms.accounting.documents.deposit_split.delete')}
      appearance={{ type: 'DIALOG', open: true, onClose: props.onClose }}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: onSubmit,
            text: t('commons.save'),
          }}
          cancelButton={{
            clickHandler: props.onClose,
          }}
        />
      }
    >
      {rowToDelete && localData && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div>{t('pms.accounting.documents.deposit_split.delete_amount', { amount: formatCurrency(rowToDelete.amount, currency) })}</div>
          </Grid>
          <Grid item xs={12}>
            <RadioGroup onChange={(e) => setDestinationRowId(e.target.value)} value={destinationRowId}>
              {localData
                ?.filter((i) => !i.documentReferenceId)
                .map((ld) => {
                  const originalFormattedAmount = formatCurrency(ld.amount, currency)
                  const updatedFormatAmount = ld.id === destinationRowId ? `(${t('commons.update_amount')} ${formatCurrency(ld.amount + rowToDelete.amount, currency)})` : ''
                  return (
                    <FormControlLabel
                      key={ld.id}
                      value={ld.id}
                      control={<Radio size="small" />}
                      label={`${getReferenceDescription({ type: ld.reference.type, description: ld.reference.description })} - ${originalFormattedAmount} ${updatedFormatAmount}`}
                    />
                  )
                })}
            </RadioGroup>
          </Grid>
        </Grid>
      )}
    </FormLayout>
  )
}
